import React, { useState, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import imageSrc from "../../assets/images/phone.png";

// Define the style type for the reveal effect
type TransformStyle = {
    transform: string;
    opacity: number; // For the fade-in effect
};

// Initial/Neutral State
const NEUTRAL_TRANSFORM = 'translateY(0px)';
const COMMON_STYLE = {
    // Slower transition for a smooth reveal (consistent with Services/Features)
    transition: 'transform 0.6s ease-out, opacity 0.6s ease-out',
    willChange: 'transform, opacity',
};

const Downloads: React.FC = () => {
  const { t } = useTranslation();

  // Ref for the entire section
  const downloadsRef = useRef<HTMLElement>(null);

  // State for the image's dynamic style
  const [imageStyle, setImageStyle] = useState<TransformStyle>({ transform: NEUTRAL_TRANSFORM, opacity: 0 });

  useLayoutEffect(() => {

    const handleScroll = () => {
      const currentSection = downloadsRef.current;
      if (!currentSection) {
        return;
      }

      const rect = currentSection.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // Calculate how far the section has moved into the viewport (0 to 1)
      const scrollProgress = 1 - (rect.top / viewportHeight);
      const clampedProgress = Math.min(1, Math.max(0, scrollProgress));


      // --- Reveal Effect Logic (Same as Services/Features) ---

      const START_OFFSET = 150; // Start position: hidden 150px below final position

      // Calculate the current offset (goes from 150 to 0)
      const currentOffset = START_OFFSET * (1 - clampedProgress);

      // Calculate the opacity (goes from 0 to 1)
      const currentOpacity = clampedProgress;


      // --- Reset logic for when element is out of view (going back up) ---
      if (clampedProgress < 0.1) {
          // Reset to initial (hidden) state
          setImageStyle({ transform: `translateY(${START_OFFSET}px)`, opacity: 0 });
          return;
      }

      // Set the dynamic style
      setImageStyle({
          transform: `translateY(${currentOffset}px)`,
          opacity: currentOpacity
      });
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section
      id="download"
      ref={downloadsRef} // Attach ref to the section
      className="banner neumorphic-container rounded-0 p-5 mb-0"
    >
      {/* The inner container likely needs the overflow: hidden property */}
      <div
        className="container gradient rounded-5 pt-5 px-5 d-flex flex-column justify-content-center align-items-center overflow-hidden"
      >
        <div className="container col-lg-12">
          <h2 className="text-center text-light">{t("downloads.title")}</h2>
          <p className="text-center text-light mb-4">{t("downloads.subtitle")}</p>
          <div className="container orange col-lg-4 p-4 mb-4">
            <h3 className="text-center text-light text box-highlight">{t("downloads.availableOn")}</h3>
            <p className="text-center text-light">{t("downloads.comingSoon")}</p>
          </div>
        </div>
        <img src={imageSrc}
            className="col-10 col-lg-4"
            alt="logo-white"
            style={{ ...COMMON_STYLE, ...imageStyle }}
        />
      </div>
    </section>
  );
}

export default Downloads;
